import React, { useState, useMemo, useEffect } from 'react';
import GroupViewMap from './components/map';
import GroupViewSidebar from './components/sidebar';
import SoftBox from 'components/SoftComponents/SoftBox';
import { setSiteFeatures } from 'utils/map';
import { processStopmonitorGroupData } from 'utils/group';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSoftUIController } from 'hooks/context';
import { Grid, Box } from "@mui/material";

import sitesAPI from "services/api/sites";
import groupAPI from "services/api/groups";
import campaignAPI from "services/api/campaign";
import bksAPI from "services/api/bks";

import Swal from 'sweetalert2';
import { useAuth0 } from '@auth0/auth0-react';
import PropTypes from 'prop-types';
import { loadingScreen } from 'utils/wrapper';
import SoftTypography from 'components/SoftComponents/SoftTypography';
import { setDeviceFeatures } from 'utils/map';
import { processDeviceData } from 'utils/device';

function GroupMapView({ user, localMap, isMapLoaded, setEditGroupId, setSplitMap }) {
    campaignAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());

    const [controller] = useSoftUIController();
    const { userPermissions, selectedOrg } = controller;
    const [hoveredGroupId, setHoveredGroupId] = useState(null);
    const [groupToDelete, setGroupToDelete] = useState(null);

    const queryClient = useQueryClient();

    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canEditGroup = userPermissions.includes('devices-groups:edit');
    const canRemoveGroup = userPermissions.includes('devices-groups:delete');

    const { refetch: findCampaignsByDeviceFilterValue } = useQuery({
        queryKey: ['findCampaignsByDeviceFilterValue', groupToDelete],
        queryFn: () => campaignAPI.findCampaignsByDeviceFilterValue(groupToDelete),
        enabled: false,
    });

    const removeGroupMutation = useMutation({
        mutationFn: (id) => groupAPI.deleteDeviceGroup(id),
        onSuccess: () => {
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Deleted!</div>',
                showConfirmButton: false,
                timer: 2000
            });
            queryClient.invalidateQueries({ queryKey: ['getAllGroups', selectedOrg] });
            setGroupToDelete(null);
        },
        onError: (e) => {
            console.log(e);
        }
    });

    const handleEdit = async (id) => {
        setEditGroupId(id);
    }

    const handleDelete = async (id) => {
        setGroupToDelete(id);

        setTimeout(async () => {
            const { data: campaignsForGroup, isError } = await findCampaignsByDeviceFilterValue();
            if (isError) {
                Swal.fire({ icon: 'error', title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:600; font-size:22px">Error</div>', html: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:14px; line-height: 1.4;">Failed to verify active campaigns.</div>' });
                return;
            }

            const activeCount = campaignsForGroup?.filter(c => c.enabled)?.length || 0;
            const totalCount = campaignsForGroup?.length || 0;

            Swal.fire({
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:600; font-size:24px">Are you sure?</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:14px; line-height: 1.4;">
                        There ${activeCount == 1 ? 'is' : 'are'} ${activeCount} active ${activeCount == 1 ? 'campaign' : 'campaigns'} that ${activeCount == 1 ? 'uses' : 'use'} this group!<br>(${totalCount} ${totalCount == 1 ? 'campaign' : 'campaigns'} in total)<br><br>
                        This will remove the group from campaigns (or the campaign entirely if it is only linked to the group).
                        </div>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dd3333',
                cancelButtonColor: '#737577',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    removeGroupMutation.mutate(id);
                }
            });
        }, 0);
    };

    const sites = useQuery({
        queryKey: ['getAllSites', selectedOrg],
        queryFn: async () => await sitesAPI.getAllSites()
    });
    const groups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => await groupAPI.getAllGroups()
    });
    const devices = useQuery({
        queryKey: ['getAllDevices', selectedOrg],
        queryFn: async () => await bksAPI.getAllDevices(),
        refetchInterval: 300 * 1000,
        refetchIntervalInBackground: true,
    });

    const visible_group_types = userPermissions.filter(prm => prm.includes('group:')).map(prm => {
        return prm.split(':')[1];
    });

    const sites_data = useMemo(() => {
        if (!sites.data) return [];
        return sites.data.map(s => setSiteFeatures(s, showAdvancedDeviceLevel)).sort((a, b) => a.properties.zIndex - b.properties.zIndex);
    }, [sites.data, showAdvancedDeviceLevel]);

    const devices_data = useMemo(() => {
        if (!devices.data) return [];
        return devices.data.map(d => setDeviceFeatures(processDeviceData(devices.dataUpdatedAt, d), showAdvancedDeviceLevel)).sort((a, b) => a.properties.zIndex - b.properties.zIndex);
    }, [devices.data, showAdvancedDeviceLevel]);

    const groups_data = useMemo(() => {
        if (!groups.data || !sites.data) return [];
        return groups.data.filter(g => visible_group_types.includes(g.type)).map(g => processStopmonitorGroupData(groups.dataUpdatedAt, g, sites.data, devices.data, showAdvancedDeviceLevel));
    }, [groups.data, groups.dataUpdatedAt, sites.data]);

    useEffect(() => {
        setHoveredGroupId(null);
    }, [selectedOrg]);

    if (sites.isLoading || groups.isLoading || devices.isLoading) {
        return (
            <Box sx={{ gridColumn: 1, gridRow: '1 / span 2', height: '100%', overflow: 'hidden', display: 'flex' }}>
                <SoftBox sx={{
                    width: { xxs: '100%', sm: '350px', md: '400px', lg: '450px', xl: '500px', xxl: '25vw' },
                    maxWidth: { xxl: '600px' },
                    flexShrink: 0,
                    height: '100%', backgroundColor: '#f8f9fa',
                    borderRight: { md: '1px solid #ddd' }, borderBottom: { xxs: '1px solid #ddd', md: 'none' }
                }}>
                    {loadingScreen(controller, true)}
                </SoftBox>
            </Box>
        );
    }

    if (sites.error || groups.error || devices.error) return "An error has occurred";

    return (
        <>
            <Box sx={{ gridColumn: 1, gridRow: '1 / span 2', height: '100%', overflow: 'hidden', display: 'flex' }}>
                <GroupViewSidebar
                    groups={groups_data}
                    hoveredGroupId={hoveredGroupId}
                    setHoveredGroupId={setHoveredGroupId}
                    canEditGroup={canEditGroup}
                    onEditGroup={handleEdit}
                    canRemoveGroup={canRemoveGroup}
                    onRemoveGroup={handleDelete}
                />
            </Box>
            <GroupViewMap
                localMap={localMap}
                isMapLoaded={isMapLoaded}
                groups={groups_data}
                sites={sites_data}
                devices={devices_data}
                hoveredGroupId={hoveredGroupId}
                setHoveredGroupId={setHoveredGroupId}
                user={user}
            />
        </>
    );
};

GroupMapView.propTypes = {
    user: PropTypes.object, localMap: PropTypes.object, isMapLoaded: PropTypes.bool,
    setEditGroupId: PropTypes.func, setSplitMap: PropTypes.func
}

export default GroupMapView;